import React, { useEffect, useState } from 'react';
import {
    Control,
    FormState,
    useForm,
    UseFormClearErrors,
    UseFormGetValues,
    UseFormResetField,
    UseFormWatch,
} from 'react-hook-form';
import { useLazyQuery, useQuery } from '@apollo/client/react';
import cn from 'clsx';
import { BasicDropDownController } from '@components-clean/core/basic-dropdown';
import ConfirmationDialog from '@components-clean/core/confirmation-dialog';
import CreatedUpdatedInfo from '@components-clean/core/created-updated-info';
import { DatePickerController } from '@components-clean/core/date-picker';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import InactiveIcon from '@components-clean/core/icons/inactive-icon/inactive-icon';
import { InfoField } from '@components-clean/core/info-field';
import TabStripLowDom from '@components-clean/core/tabstrip-low-dom';
import { TextArea, TextAreaController } from '@components-clean/core/text-area';
import { TextAreaDropdownController } from '@components-clean/core/text-area-dropdown';
import { NumericTextBoxController } from '@components-clean/core/text-box-numeric';
import { useUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import ctqStyles from '@pages-clean/risk-assessment/critical-to-quality-factors/dialogs/style.module.scss';
import { mapNamedItems } from '@pages-clean/risk-assessment/helpers';
import {
    ExtendedCompleteRiskFields,
    ExtendedCompleteRiskFieldsControl,
} from '@pages-clean/risk-assessment/risk-log/constants';
import styles from '@pages-clean/risk-assessment/risk-log/dialogs/style.module.scss';
import { NamedItemType } from 'types/graphql-global-types';
import { useBoolean, useTranslation } from '@hooks/index';
import { javascriptDateWithoutTimeFromUTC, stringDateWithoutTimeFromUtc } from '@utils/convertor';
import { RiskService } from '@utils/risk.service';
import { notEmpty } from '@utils/utility-helpers';
import { GET_NAMED_ITEMS_BY_TYPE, GET_NAMED_ITEMS_BY_TYPE_FOR_STUDY } from '@queries/common';
import { getNamedItemsByType, getNamedItemsByTypeForStudy } from '@queries/generated/common.generated';
import {
    CompleteRiskFields_Risk_controls_StudyControl as CompleteRiskFields_controls_controlPhase,
    StudyControlFields_StudyControl_owner_StudyMember as CompleteRiskFields_controls_owner,
    StudyControlFields_StudyControl_responsibleFunction_NamedItem as CompleteRiskFields_controls_responsibleFunction,
    StudyControlFields_StudyControl_type_NamedItem as CompleteRiskFields_controls_type,
} from '@queries/generated/fragments.generated';
import {
    getRisksAssignedControlIds,
    getRisksAssignedControlIdsVariables,
} from '@queries/generated/risk-assessment.generated';
import { getStudyMembers, getStudyMembersVariables } from '@queries/generated/studies.generated';
import { GET_RISKS_ASSIGNED_CONTROL_IDS } from '@queries/risk-assessment';
import { GET_STUDY_MEMBERS } from '@queries/studies';

interface AddControlProps {
    studyRef: string;
    onClose: () => void;
    onSubmit: (data: RiskControlFormInputs) => void;
    riskControl?: ExtendedCompleteRiskFieldsControl;
    risk: ExtendedCompleteRiskFields | undefined;
}

interface AdditionalInfoProps {
    risk: ExtendedCompleteRiskFields;
}

type ExtendedPhase = CompleteRiskFields_controls_controlPhase & {
    groupedBy: string;
    disabled: boolean;
};

type ExtendedType = CompleteRiskFields_controls_type & {
    groupedBy: string;
    disabled: boolean;
};

type ExtendedOwner = CompleteRiskFields_controls_owner & {
    fullName: string;
    id: string;
    groupedBy: string;
    active: boolean;
};

type ExtendedResponsibleFunction = CompleteRiskFields_controls_responsibleFunction & {
    groupedBy: string;
    disabled: boolean;
};

export type RiskControlFormInputs = {
    phase: ExtendedPhase | null;
    type: ExtendedType | null;
    owner: ExtendedOwner | null;
    description: string | null;
    dateIdentified: Date | null;
    responsibleFunction: ExtendedResponsibleFunction | null;
    reviewFrequencyPeriod: number | null;
    reviewFrequencyUnit: { id: string; text: string } | null | undefined;
    activeDate: Date | null;
    inactiveDate: Date | null;
    threshold: string | null;
    actionToBeTaken: string | null;
    purpose: string | null;
    volume: string | null;
    additionalInformation: string | null;
};

const COMPONENT_NAME = 'add-control-dialog';

const AdditionalInfo = ({ risk }: AdditionalInfoProps) => {
    const { t } = useTranslation('ram', { keyPrefix: 'controls.dialogs.addControl.riskPanel' });
    const { t: tCommon } = useTranslation('common');

    if (!risk) {
        return (
            <div className="k-h-full k-p-8">
                <InfoField type="Error" message={t('error')} />
            </div>
        );
    }

    return (
        <div className={cn(styles.addControlRiskDetails, 'k-h-full')}>
            <div className="k-d-flex">
                <div className={cn(styles.label)}>{t('fields.id')}</div>
                <div className={cn(styles.content, '!k-basis-2/3 k-flex-row k-align-items-center')}>
                    {risk.recordId}
                </div>
            </div>
            <div className="k-d-flex">
                <div className={cn(styles.label)}>{t('fields.status')}</div>
                <div className={cn(styles.content, '!k-basis-2/3 k-flex-row k-align-items-center')}>{risk?.status}</div>
            </div>
            <div className="k-d-flex">
                <div className={cn(styles.label)}>{t('fields.owner')}</div>
                <div className={cn(styles.content, styles.wrap, '!k-basis-2/3 k-flex-row k-align-items-center')}>
                    {risk?.owner ? `${risk.owner?.firstName} ${risk.owner?.lastName}` : 'N/A'}
                    {risk?.owner && !risk?.owner?.active && (
                        <div className={cn(styles.inactiveIcon, 'k-pl-sm k-ml-auto')}>
                            <InactiveIcon
                                toolTipText={tCommon('inactiveIcon')}
                                dataTestId="inactive-icon"
                                tooltipPosition="bottom"
                                className="k-mr-2"
                            />
                        </div>
                    )}
                </div>
            </div>
            <div className="k-d-flex">
                <div className={cn(styles.label)}>{t('fields.responsibleFunction')}</div>
                <div className={cn(styles.content, styles.wrap, '!k-basis-2/3 k-flex-row k-align-items-center')}>
                    {risk?.responsibleFunction ? risk.responsibleFunction?.name : 'N/A'}
                    {risk?.responsibleFunction && !risk?.responsibleFunction?.active && (
                        <div className={cn(styles.inactiveIcon, 'k-pl-sm k-ml-auto')}>
                            <InactiveIcon
                                toolTipText={tCommon('inactiveIcon')}
                                dataTestId="inactive-icon"
                                tooltipPosition="bottom"
                                className="k-mr-2"
                            />
                        </div>
                    )}
                </div>
            </div>
            <div className="k-d-flex k-flex-col k-h-full" style={{ minHeight: 0, gap: '0.5rem' }}>
                <div className="k-d-flex k-flex-col k-h-full" style={{ minHeight: 0 }}>
                    <div className={cn(styles.label)}>{t('fields.statement')}</div>
                    <div className={cn(styles.content)} style={{ overflow: 'auto' }}>
                        <TextArea
                            data-testid="add-edit-control-risk-details-statement"
                            readOnly
                            value={risk?.statement ?? undefined}
                            className="k-h-full"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

interface TabProps {
    control: Control<RiskControlFormInputs>;
}

const MainTab = ({
    control,
    getValues,
    clearErrors,
    resetField,
    formState,
    watch,
    studyRef,
    riskControl,
}: TabProps & {
    getValues: UseFormGetValues<RiskControlFormInputs>;
    clearErrors: UseFormClearErrors<RiskControlFormInputs>;
    resetField: UseFormResetField<RiskControlFormInputs>;
    formState: FormState<RiskControlFormInputs>;
    watch: UseFormWatch<RiskControlFormInputs>;
    studyRef: string;
    riskControl: ExtendedCompleteRiskFieldsControl | undefined;
}) => {
    const { t } = useTranslation('ram', { keyPrefix: 'controls.dialogs.addControl' });

    const reviewFrequencyPeriod = watch('reviewFrequencyPeriod');
    const reviewFrequencyUnit = watch('reviewFrequencyUnit');

    const { data: phaseData, loading: loadingPhaseData } = useQuery<getNamedItemsByType>(GET_NAMED_ITEMS_BY_TYPE, {
        variables: { type: NamedItemType.CONTROL_PHASE },
    });

    const { data: typeData, loading: loadingTypeData } = useQuery<getNamedItemsByType>(GET_NAMED_ITEMS_BY_TYPE, {
        variables: { type: NamedItemType.CONTROL_TYPE },
    });

    const [owners, setOwners] = useState<
        ({ fullName: string; id: string; groupedBy: string; active: boolean } | null)[] | undefined
    >([]);

    const [loadOwnerData, { data: ownerData, loading: loadingOwners }] = useLazyQuery<
        getStudyMembers,
        getStudyMembersVariables
    >(GET_STUDY_MEMBERS);

    const { data: responsibleFunctionsData, loading: loadingResponsibleFunctions } =
        useQuery<getNamedItemsByTypeForStudy>(GET_NAMED_ITEMS_BY_TYPE_FOR_STUDY, {
            variables: {
                studyId: studyRef,
                type: NamedItemType.RESPONSIBLE_FUNCTION,
            },
        });

    const reviewFrequencyUnits: { id: string; text: string }[] = Object.assign([], RiskService.timeFrequencyListObject);

    useEffect(() => {
        if (!reviewFrequencyPeriod && !reviewFrequencyUnit) {
            clearErrors('reviewFrequencyPeriod');
            clearErrors('reviewFrequencyUnit');
        }
    }, [reviewFrequencyPeriod, reviewFrequencyUnit]);

    useEffect(() => {
        if (
            riskControl?.controlPhase !== undefined &&
            !formState.dirtyFields.phase &&
            getValues('phase') !== riskControl.controlPhase
        ) {
            const phaseValue = riskControl?.controlPhaseId
                ? (((phaseData?.namedItemsByType
                      ? mapNamedItems(
                            phaseData.namedItemsByType,
                            riskControl?.controlPhase?.name ? [riskControl.controlPhase.name] : undefined,
                        )
                      : []
                  )?.find((p) => p?.id === riskControl?.controlPhaseId) as ExtendedPhase) ?? null)
                : null;
            resetField('phase', { defaultValue: phaseValue });
        }
    }, [riskControl?.controlPhaseId]);
    useEffect(() => {
        if (riskControl?.type !== undefined && !formState.dirtyFields.type && getValues('type') !== riskControl.type) {
            const typeValue = riskControl?.typeId
                ? (((typeData?.namedItemsByType
                      ? mapNamedItems(
                            typeData.namedItemsByType,
                            riskControl?.type?.name ? [riskControl.type.name] : undefined,
                        )
                      : []
                  )?.find((type) => type?.id === riskControl?.typeId) as ExtendedType) ?? null)
                : null;
            resetField('type', { defaultValue: typeValue });
        }
    }, [riskControl?.type]);
    useEffect(() => {
        if (
            riskControl?.owner !== undefined &&
            !formState.dirtyFields.owner &&
            getValues('owner') !== riskControl.owner
        ) {
            const ownerValue = riskControl?.ownerId
                ? ((owners?.find((o) => o?.id === riskControl?.ownerId) as ExtendedOwner) ?? null)
                : null;
            resetField('owner', { defaultValue: ownerValue });
        }
    }, [owners]);
    useEffect(() => {
        if (
            riskControl?.responsibleFunction !== undefined &&
            !formState.dirtyFields.responsibleFunction &&
            getValues('responsibleFunction') !== riskControl.responsibleFunction
        ) {
            const responsibleFunctionValue = riskControl?.responsibleFunctionId
                ? (((responsibleFunctionsData?.namedItemsByTypeForStudy
                      ? mapNamedItems(
                            responsibleFunctionsData.namedItemsByTypeForStudy,
                            riskControl?.responsibleFunction?.name
                                ? [riskControl?.responsibleFunction.name]
                                : undefined,
                        )
                      : []
                  )?.find((rf) => rf?.id === riskControl?.responsibleFunctionId) as ExtendedResponsibleFunction) ??
                  null)
                : null;
            resetField('responsibleFunction', { defaultValue: responsibleFunctionValue });
        }
    }, [riskControl?.responsibleFunctionId]);

    useEffect(() => {
        if (ownerData?.studyMembers) {
            const studyMembers = ownerData.studyMembers
                .filter((user) => user)
                .sort((aOwner, bOwner) => {
                    if (aOwner?.active !== bOwner?.active) {
                        return aOwner?.active ? -1 : 1;
                    }
                    const aName = `${aOwner?.firstName?.toLowerCase() ?? ''} ${aOwner?.lastName?.toLowerCase() ?? ''}`;
                    const bName = `${bOwner?.firstName?.toLowerCase() ?? ''} ${bOwner?.lastName?.toLowerCase() ?? ''}`;
                    return aName.localeCompare(bName);
                })
                .map((user) => ({
                    fullName: `${user?.firstName} ${user?.lastName}`,
                    id: user?.id as string,
                    groupedBy: user?.active ? 'Active' : 'Inactive',
                    disabled: riskControl?.convertedOwner
                        ? riskControl.convertedOwner.name?.toLowerCase() ===
                          `${user?.firstName?.toLowerCase()} ${user?.lastName?.toLowerCase()}`
                            ? false
                            : !user?.active
                        : !user?.active,
                    active: !!user?.active,
                }));
            setOwners(studyMembers);
        }
    }, [ownerData?.studyMembers]);

    useEffect(() => {
        loadOwnerData({ variables: { studyId: studyRef } }).catch((err) => window.console.error(err));
    }, []);

    return (
        <div className={cn('k-d-flex k-flex-col k-w-full k-pt-md')}>
            <div className={cn('k-shrink-0 k-d-flex k-justify-content-center k-pb-sm', styles.formFieldGap)}>
                <div className={cn('!k-basis-1/2', styles.minWidthFix, styles.dropdownFix)}>
                    <BasicDropDownController
                        control={control}
                        name="phase"
                        label={t('fields.phase')}
                        data-testid="risk-control-field-phase"
                        data={
                            phaseData?.namedItemsByType
                                ? mapNamedItems(
                                      phaseData.namedItemsByType,
                                      riskControl?.controlPhase?.name ? [riskControl.controlPhase.name] : undefined,
                                  )
                                : []
                        }
                        textField="name"
                        groupField="groupedBy"
                        activeField="active"
                        disabled={loadingPhaseData}
                        loading={loadingPhaseData}
                        dataItemKey="id"
                        filterable
                        allowClear
                    />
                </div>
                <div className={cn('!k-basis-1/2')} />
            </div>
            <div className={cn('k-shrink-0 k-d-flex k-justify-content-center k-pb-sm', styles.formFieldGap)}>
                <div className={cn('!k-basis-1/2', styles.minWidthFix, styles.dropdownFix)}>
                    <BasicDropDownController
                        control={control}
                        name="type"
                        label={t('fields.type')}
                        data-testid="risk-control-field-type"
                        data={
                            typeData?.namedItemsByType
                                ? mapNamedItems(
                                      typeData.namedItemsByType,
                                      riskControl?.type?.name ? [riskControl.type.name] : undefined,
                                  )
                                : []
                        }
                        textField="name"
                        groupField="groupedBy"
                        activeField="active"
                        disabled={loadingTypeData}
                        loading={loadingTypeData}
                        dataItemKey="id"
                        filterable
                        allowClear
                    />
                </div>
                <div className={cn('!k-basis-1/2', styles.minWidthFix, styles.dropdownFix)}>
                    <BasicDropDownController
                        control={control}
                        name="owner"
                        label={t('fields.owner')}
                        data-testid="risk-control-field-owner"
                        data={owners ?? []}
                        loading={loadingOwners}
                        dataItemKey="id"
                        textField="fullName"
                        groupField="groupedBy"
                        activeField="active"
                        filterable
                        allowClear
                    />
                </div>
            </div>
            <div className="k-d-flex k-flex-auto k-justify-content-center">
                <TextAreaController
                    className={styles.textAreaOverrides}
                    maxLength={3000}
                    control={control}
                    name="description"
                    data-testid="risk-control-field-description"
                    label={t('fields.description')}
                    resizable="none"
                    required
                />
            </div>
            <div className={cn('k-shrink-0 k-d-flex k-justify-content-center k-pb-sm', styles.formFieldGap)}>
                <div className={cn('!k-basis-1/2')}>
                    <DatePickerController
                        control={control}
                        name="dateIdentified"
                        label={t('fields.dateIdentified')}
                        data-testid="risk-control-field-date-identified"
                        fillMode="solid"
                    />
                </div>
                <div className={cn('!k-basis-1/2', styles.minWidthFix, styles.dropdownFix)}>
                    <BasicDropDownController
                        control={control}
                        name="responsibleFunction"
                        label={t('fields.responsibleFunction')}
                        data-testid="risk-action-field-responsible-function"
                        data={
                            responsibleFunctionsData?.namedItemsByTypeForStudy
                                ? mapNamedItems(
                                      responsibleFunctionsData.namedItemsByTypeForStudy,
                                      riskControl?.responsibleFunction?.name
                                          ? [riskControl?.responsibleFunction.name]
                                          : undefined,
                                  )
                                : []
                        }
                        disabled={loadingResponsibleFunctions}
                        loading={loadingResponsibleFunctions}
                        dataItemKey="id"
                        textField="name"
                        groupField="groupedBy"
                        activeField="active"
                        filterable
                        allowClear
                    />
                </div>
            </div>
            <div className={cn('k-pb-sm', styles.formFieldGap)}>
                <span className={cn('k-label')}>{t('fields.reviewFrequency')}</span>
                <div className="k-d-flex k-gap-2">
                    <div className={cn(ctqStyles.minWidthFix)}>
                        <NumericTextBoxController
                            control={control}
                            name="reviewFrequencyPeriod"
                            data-testid="risk-field-review-frequency-period"
                            className={cn(ctqStyles.reviewFrequencyPeriod)}
                            min={RiskService.reviewFrequencyNumberMin}
                            max={RiskService.reviewFrequencyNumberMax}
                            format="0"
                            required={!!reviewFrequencyUnit}
                        />
                    </div>
                    <div className={cn(ctqStyles.minWidthFix, ctqStyles.dropdownFix)}>
                        <BasicDropDownController
                            control={control}
                            name="reviewFrequencyUnit"
                            data-testid="risk-field-review-frequency-unit"
                            textField="text"
                            dataItemKey="id"
                            filterable
                            allowClear
                            data={reviewFrequencyUnits ?? []}
                            className={cn(ctqStyles.reviewFrequencyUnits)}
                            required={!!reviewFrequencyPeriod}
                        />
                    </div>
                </div>
            </div>
            <div className={cn('k-shrink-0 k-d-flex k-justify-content-center k-pb-sm', styles.formFieldGap)}>
                <div className={cn('!k-basis-1/2')}>
                    <DatePickerController
                        control={control}
                        name="activeDate"
                        label={t('fields.activeDate')}
                        data-testid="risk-control-field-active-date"
                        fillMode="solid"
                    />
                </div>
                <div className={cn('!k-basis-1/2')}>
                    <DatePickerController
                        control={control}
                        name="inactiveDate"
                        label={t('fields.inactiveDate')}
                        data-testid="risk-control-field-inactive-date"
                        fillMode="solid"
                    />
                </div>
            </div>
            <div className="k-d-flex k-flex-auto k-justify-content-center k-pb-sm">
                <TextAreaDropdownController
                    className={styles.textAreaOverrides}
                    maxLength={3000}
                    control={control}
                    name="threshold"
                    data-testid="risk-control-field-threshold"
                    label={t('fields.threshold')}
                    resizable="none"
                />
            </div>
            <div className="k-d-flex k-flex-auto k-justify-content-center k-pb-sm">
                <TextAreaDropdownController
                    className={styles.textAreaOverrides}
                    maxLength={3000}
                    control={control}
                    name="actionToBeTaken"
                    data-testid="risk-control-field-action-to-be-taken"
                    label={t('fields.actionToBeTaken')}
                    resizable="none"
                />
            </div>
        </div>
    );
};

const MonitoringTab = ({ control }: TabProps) => {
    const { t } = useTranslation('ram', { keyPrefix: 'controls.dialogs.addControl' });

    return (
        <div className={cn('k-d-flex k-flex-col k-w-full k-pt-md')}>
            <div className="k-d-flex k-flex-auto k-justify-content-center">
                <TextAreaController
                    className={styles.monitoringTabTextAreaOverrides}
                    maxLength={3000}
                    control={control}
                    name="purpose"
                    data-testid="risk-control-field-purpose"
                    label={t('fields.purpose')}
                    resizable="none"
                />
            </div>
            <div className="k-d-flex k-flex-auto k-justify-content-center">
                <TextAreaController
                    className={styles.monitoringTabTextAreaOverrides}
                    maxLength={3000}
                    control={control}
                    name="volume"
                    data-testid="risk-control-field-volume"
                    label={t('fields.volume')}
                    resizable="none"
                />
            </div>
            <div className="k-d-flex k-flex-auto k-justify-content-center k-pb-sm">
                <TextAreaController
                    className={styles.monitoringTabTextAreaOverrides}
                    maxLength={3000}
                    control={control}
                    name="additionalInformation"
                    data-testid="risk-control-field-additional-information"
                    label={t('fields.additionalInformation')}
                    resizable="none"
                />
            </div>
        </div>
    );
};

const AddControlDialog = ({ studyRef, onClose, onSubmit, riskControl, risk }: AddControlProps) => {
    const {
        t,
        i18n: { t: tRoot },
    } = useTranslation('ram', { keyPrefix: 'controls.dialogs.addControl' });

    const { setComponentHasChanges, componentHasChanges, confirmDiscardChanges } = useUnsavedChangesContext();

    const showInactiveDateDialog = useBoolean(false);

    const { control, handleSubmit, resetField, reset, getValues, clearErrors, formState, watch } =
        useForm<RiskControlFormInputs>({
            mode: 'all',
            defaultValues: {
                description: riskControl?.description ?? '',
                dateIdentified: riskControl?.dateIdentified
                    ? javascriptDateWithoutTimeFromUTC(riskControl.dateIdentified)
                    : null,
                reviewFrequencyPeriod: riskControl?.reviewFrequencyPeriod
                    ? riskControl?.reviewFrequencyPeriod > 0
                        ? riskControl?.reviewFrequencyPeriod
                        : null
                    : null,
                reviewFrequencyUnit: riskControl?.reviewFrequencyUnits
                    ? RiskService.timeFrequencyListObject.find((u) => u.id === riskControl.reviewFrequencyUnits)
                    : null,
                activeDate: riskControl?.activeDate ? javascriptDateWithoutTimeFromUTC(riskControl.activeDate) : null,
                inactiveDate: riskControl?.inactiveDate
                    ? javascriptDateWithoutTimeFromUTC(riskControl.inactiveDate)
                    : null,
                threshold: riskControl?.threshold ?? '',
                actionToBeTaken: riskControl?.actionToBeTaken ?? '',
                purpose: riskControl?.purpose ?? '',
                volume: riskControl?.volume ?? '',
                additionalInformation: riskControl?.additionalInformation ?? '',
            },
        });

    const { data: risksAssignedControlIdsData, loading: risksAssignedControlIdsLoading } = useQuery<
        getRisksAssignedControlIds,
        getRisksAssignedControlIdsVariables
    >(GET_RISKS_ASSIGNED_CONTROL_IDS, {
        variables: { studyId: studyRef },
    });

    useEffect(() => {
        reset(
            {
                ...getValues(),
            },
            {
                keepErrors: true,
                keepDirty: true,
            },
        );
    }, [formState.isReady]);

    const createdBy = `${stringDateWithoutTimeFromUtc(riskControl?.created, 'DD-MMM-YYYY')} by ${riskControl?.createdBy}`;
    const updatedBy = `${stringDateWithoutTimeFromUtc(riskControl?.updated, 'DD-MMM-YYYY')} by ${riskControl?.updatedBy}`;

    const dialogTitle = riskControl
        ? `${tRoot('common:edit')} ${t('title')}: ${riskControl.recordId}`
        : `${tRoot('common:add')} ${t('title')}`;
    const primaryBtnLabel = riskControl ? tRoot('common:update') : tRoot('common:add');

    useEffect(() => {
        setComponentHasChanges(COMPONENT_NAME, formState.isDirty);
        return () => {
            setComponentHasChanges(COMPONENT_NAME, false);
        };
    }, [formState.isDirty]);

    const onCloseHandler = async () => {
        if (componentHasChanges(COMPONENT_NAME) && Object.keys(formState.dirtyFields).length > 0) {
            const confirmed = await confirmDiscardChanges('add-control-unsaved-dialog');
            if (!confirmed) {
                return;
            }
            setComponentHasChanges(COMPONENT_NAME, false);
        }
        onClose();
    };

    const riskCount =
        riskControl && !risksAssignedControlIdsLoading && risksAssignedControlIdsData?.risks
            ? risksAssignedControlIdsData.risks
                  .filter(notEmpty)
                  .reduce(
                      (count: number, r: { controlIds: string[] | null }) =>
                          r.controlIds?.includes(riskControl.id) ? count + 1 : count,
                      0,
                  )
            : 0;

    const handleDialogSubmit = async () => {
        const inactiveDateHasValue = !!getValues().inactiveDate;
        const inactiveDateIsDirty = formState.dirtyFields.inactiveDate;
        if (inactiveDateHasValue && inactiveDateIsDirty) {
            showInactiveDateDialog.setTrue();
            return;
        }
        await handleSubmit(onSubmit)();
    };

    if (risksAssignedControlIdsLoading) return;

    return (
        <DraggableDialog
            className={cn(styles.kWindowContent)}
            dataTestId="add-control-dialog"
            title={dialogTitle}
            onClose={onCloseHandler}
            onSubmit={handleDialogSubmit}
            primaryBtnLabel={primaryBtnLabel}
            primaryBtnDisabled={!formState.isValid || !formState.isDirty || formState.isSubmitting}
            initialHeight={riskControl ? (riskCount > 0 ? 765 : 705) : 620}
            initialWidth={542}
            createdUpdatedInfo={
                riskControl ? <CreatedUpdatedInfo created={createdBy} updated={updatedBy} /> : undefined
            }
            createdUpdatedBackgroundColour="gray"
            bodyPadding="none"
            additionalInfoWidth={350}
            additionalInfoBtnLabel={t('riskPanel.title')}
            additionalInfo={risk ? <AdditionalInfo risk={risk} /> : undefined}
        >
            <div className={cn('k-h-full k-w-full k-pl-md k-pr-md k-pb-md k-pt-sm', styles.rightBorderStyle)}>
                {riskCount && riskCount > 0 ? (
                    <div className="k-pb-md">
                        <InfoField
                            type="Warning"
                            message={t('warnings.associatedRisks', {
                                count: riskCount,
                            })}
                        />
                    </div>
                ) : (
                    ''
                )}
                {riskControl && (
                    <div className={cn(styles.editActionMetaData, 'k-py-xs k-pl-md k-mb-md')}>
                        <div className="k-d-flex k-gap-3">
                            <div className="k-font-medium">{t('metaDataLabels.status')}</div>
                            <div className={cn(styles.editControlDialogTopInfoLabel)}>{riskControl.status}</div>
                        </div>
                    </div>
                )}
                <form id="add-edit-risk" className="k-w-full">
                    <TabStripLowDom
                        keepTabDataMounted
                        data={[
                            {
                                title: t('tabs.main'),
                                content: (
                                    <MainTab
                                        control={control}
                                        getValues={getValues}
                                        clearErrors={clearErrors}
                                        resetField={resetField}
                                        formState={formState}
                                        watch={watch}
                                        studyRef={studyRef}
                                        riskControl={riskControl}
                                    />
                                ),
                                dataTestId: 'add-control-dialog-main-tab',
                            },
                            {
                                title: t('tabs.monitoring'),
                                content: <MonitoringTab control={control} />,
                                dataTestId: 'add-control-dialog-monitoring-tab',
                            },
                        ]}
                    />
                </form>
            </div>
            {showInactiveDateDialog.value && (
                <ConfirmationDialog
                    dataTestId="confirm-inactive-date-dialog"
                    title={t('warnings.inactiveDate.title')}
                    onSubmit={async () => {
                        await handleSubmit(onSubmit)();
                        showInactiveDateDialog.setFalse();
                    }}
                    onClose={() => {
                        showInactiveDateDialog.setFalse();
                    }}
                    primaryBtnLabel={tRoot('common:confirm')}
                    secondaryBtnLabel={tRoot('common:cancel')}
                    primaryBtnDisabled={false}
                    ignoreReadonlyCtx
                    width={400}
                    height={200}
                >
                    <div className="k-d-flex k-flex-col k-h-full">
                        <div>
                            <InfoField type="Warning" message={t('warnings.inactiveDate.body')} />
                        </div>
                        <div className="k-d-flex k-justify-content-center !k-align-items-center" style={{ flex: 1 }}>
                            {t('warnings.inactiveDate.confirmation')}
                        </div>
                    </div>
                </ConfirmationDialog>
            )}
        </DraggableDialog>
    );
};

export default AddControlDialog;
